import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";
import styled from "styled-components";

const CreatorProfilePage = () => {
  const [activeTab, setActiveTab] = useState("about");

  const location = useLocation();

  
  const profileData = location?.state?.data || {};
  

  const TabContent = ({ tab }) => {
    // Check if profile data exists
    if (!profileData || Object.keys(profileData).length === 0) {
      return (
        <AboutSection>
          <SectionTitle>No Data Available</SectionTitle>
          <AboutDescription>
            Profile information is currently unavailable. Please check back
            later.
          </AboutDescription>
        </AboutSection>
      );
    }

    switch (tab) {
      case "about":
        return (
          <AboutSection>
            <SectionTitle>About Me</SectionTitle>
            <AboutDescription>
              {profileData?.name
                ? `${profileData.name} is a professional with ${
                    profileData?.experience_years || 0
                  }+ years of experience.`
                : "No profile information available."}
            </AboutDescription>

            {profileData?.expertise &&
            profileData.expertise.length > 0 ? (
              <>
                <SectionTitle>Expertise</SectionTitle>
                <LanguageContainer>
                  {profileData.expertise.map((exp, index) => (
                    <LanguageTag key={exp.id || index}>{exp.name}</LanguageTag>
                  ))}
                </LanguageContainer>
              </>
            ) : (
              <>
                <SectionTitle>Expertise</SectionTitle>
                <AboutDescription>
                  No expertise information available.
                </AboutDescription>
              </>
            )}

            {profileData?.specialization &&
            profileData.specialization.length > 0 ? (
              <>
                <SectionTitle>Specializations</SectionTitle>
                <SpecializationGrid>
                  {profileData.specialization.map((spec, index) => (
                    <SpecializationCard key={spec.id || index}>
                      <SpecializationIcon>🎯</SpecializationIcon>
                      <SpecializationTitle>{spec.name}</SpecializationTitle>
                    </SpecializationCard>
                  ))}
                </SpecializationGrid>
              </>
            ) : (
              <>
                <SectionTitle>Specializations</SectionTitle>
                <AboutDescription>No specializations listed.</AboutDescription>
              </>
            )}

            {profileData?.skills && profileData.skills.length > 0 ? (
              <>
                <SectionTitle>Skills</SectionTitle>
                <ObjectivesList>
                  {profileData.skills.map((skill, index) => (
                    <ObjectiveItem key={skill.id || index}>
                      {skill.title}
                    </ObjectiveItem>
                  ))}
                </ObjectivesList>
              </>
            ) : (
              <>
                <SectionTitle>Skills</SectionTitle>
                <AboutDescription>No skills listed.</AboutDescription>
              </>
            )}
          </AboutSection>
        );

      case "experience":
        return (
          <ExperienceSection>
            {profileData?.experience &&
            profileData.experience.length > 0 ? (
              profileData.experience.map((job, index) => (
                <ExperienceCard key={job.id || index}>
                  <ExperienceHeader>
                    <ExperienceIcon>🏥</ExperienceIcon>
                    <ExperienceTitle>{job.title}</ExperienceTitle>
                  </ExperienceHeader>
                  <ExperienceCompany>{job.company}</ExperienceCompany>
                  <ExperiencePeriod>
                    {new Date(job.start_date).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                    })}{" "}
                    -
                    {new Date(job.end_date).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                    })}
                  </ExperiencePeriod>
                  <ExperienceDescription>
                    {job.description}
                  </ExperienceDescription>
                </ExperienceCard>
              ))
            ) : (
              <AboutDescription>No work experience available.</AboutDescription>
            )}

            {profileData?.certification &&
            profileData.certification.length > 0 ? (
              <CertificationsSection>
                <SectionTitle>Certifications</SectionTitle>
                <CertificationsList>
                  {profileData.certification.map((cert, index) => (
                    <CertificationItem key={cert.id || index}>
                      {cert.name} - {cert.issuer}
                    </CertificationItem>
                  ))}
                </CertificationsList>
              </CertificationsSection>
            ) : (
              <CertificationsSection>
                <SectionTitle>Certifications</SectionTitle>
                <AboutDescription>
                  No certifications available.
                </AboutDescription>
              </CertificationsSection>
            )}

            {profileData?.volunteer &&
            profileData.volunteer.length > 0 ? (
              <CertificationsSection>
                <SectionTitle>Volunteer Experience</SectionTitle>
                <CertificationsList>
                  {profileData.volunteer.map((vol, index) => (
                    <CertificationItem key={vol.id || index}>
                      {vol.role} at {vol.organisation} - {vol.cause}
                    </CertificationItem>
                  ))}
                </CertificationsList>
              </CertificationsSection>
            ) : (
              <CertificationsSection>
                <SectionTitle>Volunteer Experience</SectionTitle>
                <AboutDescription>
                  No volunteer experience listed.
                </AboutDescription>
              </CertificationsSection>
            )}
          </ExperienceSection>
        );

      case "education":
        return (
          <EducationSection>
            {profileData?.education &&
            profileData.education.length > 0 ? (
              profileData.education.map((edu, index) => (
                <EducationCard key={edu.id || index}>
                  <EducationHeader>
                    <EducationIcon>🎓</EducationIcon>
                    <EducationDegree>{edu.degree}</EducationDegree>
                  </EducationHeader>
                  <EducationInstitution>{edu.school}</EducationInstitution>
                  <EducationPeriod>
                    {new Date(edu.start_date).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                    })}{" "}
                    -
                    {new Date(edu.end_date).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                    })}
                  </EducationPeriod>
                  {edu.field_of_study && (
                    <EducationDescription>
                      Field of Study: {edu.field_of_study}
                    </EducationDescription>
                  )}
                  {edu.description && (
                    <EducationDescription>
                      {edu.description}
                    </EducationDescription>
                  )}
                </EducationCard>
              ))
            ) : (
              <AboutDescription>
                No education information available.
              </AboutDescription>
            )}

            {profileData?.achievement &&
            profileData.achievement.length > 0 ? (
              <AwardsSection>
                <SectionTitle>Awards & Recognition</SectionTitle>
                <AwardsList>
                  {profileData.achievement.map((award, index) => (
                    <AwardItem key={award.id || index}>
                      {award.name} - {award.issuer} (
                      {new Date(award.issue_date).getFullYear()})
                    </AwardItem>
                  ))}
                </AwardsList>
              </AwardsSection>
            ) : (
              <AwardsSection>
                <SectionTitle>Awards & Recognition</SectionTitle>
                <AboutDescription>
                  No awards or achievements listed.
                </AboutDescription>
              </AwardsSection>
            )}
          </EducationSection>
        );

      default:
        return null;
    }
  };

  return (
    <PageContainer>
      <MainLayout>
        <LeftColumn>
          <ProfileCard>
            <ProfileImage
              src={
                profileData?.profile_pic || "https://via.placeholder.com/100"
              }
              alt={profileData?.name || "Profile"}
            />
            <ProfileInfo>
              <ProfileName>
                {profileData?.name || "No Name Available"}
              </ProfileName>
              <ProfileTitle>
                {profileData?.designation ||
                  profileData?.professional_title ||
                  "No Title Available"}
              </ProfileTitle>
              <ProfileExperience>
                {profileData?.experience_years
                  ? `${profileData.experience_years}+ years experience`
                  : "Experience not specified"}
              </ProfileExperience>
              <ProfileSessions>
                <SessionIcon>📍</SessionIcon>
                {profileData?.location || "Location not specified"}
              </ProfileSessions>
              {profileData?.expertise &&
              profileData.expertise.length > 0 ? (
                <SpecialtyTags>
                  {profileData.expertise.slice(0, 4).map((exp, index) => (
                    <SpecialtyTag
                      key={exp.id || index}
                      color={getSpecialtyColor(index)}
                    >
                      {exp.name}
                    </SpecialtyTag>
                  ))}
                </SpecialtyTags>
              ) : (
                <AboutDescription
                  style={{ marginTop: "10px", fontSize: "13px" }}
                >
                  No expertise areas listed
                </AboutDescription>
              )}
            </ProfileInfo>
          </ProfileCard>

          <TabsContainer>
            <Tab
              active={activeTab === "about"}
              onClick={() => setActiveTab("about")}
            >
              About
            </Tab>
            <Tab
              active={activeTab === "experience"}
              onClick={() => setActiveTab("experience")}
            >
              Experience
            </Tab>
            <Tab
              active={activeTab === "education"}
              onClick={() => setActiveTab("education")}
            >
              Education
            </Tab>
          </TabsContainer>

          <ContentContainer>
            <TabContent tab={activeTab} />
          </ContentContainer>
        </LeftColumn>

        <RightColumn>
          <BookingCard>
            <BookingTitle>Book a Session</BookingTitle>
            {/* <BookingOptions>
              {profileData.bookingOptions.map((option, index) => (
                <BookingOption key={index}>
                  <OptionIcon>{getOptionIcon(option.type)}</OptionIcon>
                  <OptionDetails>
                    <OptionType>{option.type}</OptionType>
                    <OptionDuration>{option.duration}</OptionDuration>
                  </OptionDetails>
                  <OptionPrice>{option.price}</OptionPrice>
                </BookingOption>
              ))}
            </BookingOptions> */}

            <WhatToExpect>
              <WhatToExpectTitle>What to expect:</WhatToExpectTitle>
              <WhatToExpectList>
                {[
                  "Confidential one-on-one session",
                  "Professional guidance & support",
                  "Personalized treatment plan",
                  "Follow-up recommendations",
                ].map((expectation, index) => (
                  <WhatToExpectItem key={index}>{expectation}</WhatToExpectItem>
                ))}
              </WhatToExpectList>
              <NoteBox>
                <NoteTitle>Note:</NoteTitle>
                All sessions are completely confidential and secure. Your
                privacy is our priority.
              </NoteBox>
            </WhatToExpect>

            <BookButton>Book Session</BookButton>
          </BookingCard>
        </RightColumn>
      </MainLayout>
    </PageContainer>
  );
};

// Helper functions
const getSpecialtyColor = (index) => {
  const colors = ["#FFE3E3", "#E3F2FD", "#F3E5F5", "#E8F5E9"];
  return colors[index % colors.length];
};

const getOptionIcon = (type) => {
  switch (type) {
    case "Chat Session":
      return "💬";
    case "Voice Call":
      return "📞";
    case "Video Call":
      return "📹";
    default:
      return "💬";
  }
};

// Styled Components
const PageContainer = styled.div`
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  max-width: 80%;
  margin: auto;
  margin-top: 100px;
  padding: 20px;
  background-color: #f9f9f9;
`;

const MainLayout = styled.div`
  display: flex;
  gap: 20px;
  align-items: flex-start;

  @media (max-width: 968px) {
    flex-direction: column;
  }
`;

const LeftColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
`;

const RightColumn = styled.div`
  width: 350px;
  position: sticky;
  top: 20px;

  @media (max-width: 968px) {
    width: 100%;
    position: static;
  }
`;

const ProfileCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  gap: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const ProfileImage = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #e3f2fd;
`;

const ProfileInfo = styled.div`
  flex: 1;
`;

const ProfileName = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0 0 5px 0;
  text-transform: capitalize;
`;

const ProfileTitle = styled.p`
  font-size: 16px;
  color: #666;
  margin: 0 0 5px 0;
`;

const ProfileExperience = styled.p`
  font-size: 14px;
  color: #888;
  margin: 0 0 10px 0;
`;

const ProfileSessions = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0 0 15px 0;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const SessionIcon = styled.span`
  font-size: 16px;
`;

const SpecialtyTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
`;

const SpecialtyTag = styled.span`
  background-color: ${(props) => props.color};
  color: #333;
  padding: 5px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
`;

const BookingCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const BookingTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0 0 15px 0;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
`;

const BookingOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
`;

const BookingOption = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #eee;
  transition: all 0.2s ease;

  &:hover {
    background: #f1f3f5;
    transform: translateY(-1px);
  }
`;

const OptionIcon = styled.span`
  font-size: 20px;
  margin-right: 10px;
`;

const OptionDetails = styled.div`
  flex: 1;
  margin-right: 10px;
`;

const OptionType = styled.div`
  font-weight: 500;
  color: #333;
`;

const OptionDuration = styled.div`
  font-size: 12px;
  color: #888;
`;

const OptionPrice = styled.div`
  font-weight: 600;
  color: #333;
  font-size: 16px;
`;

const WhatToExpect = styled.div`
  margin: 20px 0;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
`;

const WhatToExpectTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0 0 10px 0;
`;

const WhatToExpectList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const WhatToExpectItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 8px;
  font-size: 14px;
  color: #666;

  &::before {
    content: "□";
    margin-right: 8px;
    color: #888;
  }
`;

const NoteBox = styled.div`
  background: #e3f2fd;
  border-left: 4px solid #2196f3;
  padding: 12px 15px;
  border-radius: 6px;
  margin-top: 15px;
  font-size: 13px;
  color: #333;
`;

const NoteTitle = styled.span`
  font-weight: 600;
  color: #2196f3;
  margin-right: 5px;
`;

const BookButton = styled.button`
  width: 100%;
  padding: 12px;
  background: #ff3d00;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #e63900;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const TabsContainer = styled.div`
  display: flex;
  border-bottom: 1px solid #eee;
  margin-bottom: 20px;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
`;

const Tab = styled.div`
  flex: 1;
  padding: 15px 20px;
  text-align: center;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  background: ${(props) => (props.active ? "#ff3d00" : "white")};
  color: ${(props) => (props.active ? "white" : "#333")};
  border-bottom: ${(props) => (props.active ? "3px solid #e63900" : "none")};

  &:hover {
    background: ${(props) => (props.active ? "#e63900" : "#f8f9fa")};
  }
`;

const ContentContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 20px 0 15px 0;
  padding-bottom: 5px;
  border-bottom: 2px solid #eee;
`;

const AboutSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const AboutDescription = styled.p`
  font-size: 14px;
  color: #666;
  line-height: 1.5;
`;

const LanguageContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const LanguageTag = styled.span`
  background: #f1f3f5;
  color: #333;
  padding: 5px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
`;

const SpecializationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 15px;
  margin-top: 10px;
`;

const SpecializationCard = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 15px;
  border: 1px solid #eee;
  transition: all 0.2s ease;

  &:hover {
    background: #f1f3f5;
    transform: translateY(-2px);
  }
`;

const SpecializationIcon = styled.span`
  font-size: 24px;
  margin-right: 10px;
`;

const SpecializationTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0 0 5px 0;
`;

const SpecializationDescription = styled.p`
  font-size: 13px;
  color: #666;
  line-height: 1.4;
`;

const ObjectivesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ObjectiveItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 8px;
  font-size: 14px;
  color: #666;

  &::before {
    content: "□";
    margin-right: 8px;
    color: #888;
  }
`;

const ExperienceSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ExperienceCard = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 15px;
  border: 1px solid #eee;
  margin-bottom: 15px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ExperienceHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 5px;
`;

const ExperienceIcon = styled.span`
  font-size: 20px;
  margin-right: 10px;
`;

const ExperienceTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const ExperienceCompany = styled.p`
  font-size: 14px;
  color: #666;
  margin: 5px 0;
  font-weight: 500;
`;

const ExperiencePeriod = styled.p`
  font-size: 13px;
  color: #888;
  margin: 5px 0 10px 0;
`;

const ExperienceDescription = styled.p`
  font-size: 14px;
  color: #666;
  line-height: 1.5;
`;

const CertificationsSection = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 15px;
  margin-top: 20px;
`;

const CertificationsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const CertificationItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 8px;
  font-size: 14px;
  color: #666;

  &::before {
    content: "□";
    margin-right: 8px;
    color: #888;
  }
`;

const EducationSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const EducationCard = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 15px;
  border: 1px solid #eee;
  margin-bottom: 15px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const EducationHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 5px;
`;

const EducationIcon = styled.span`
  font-size: 20px;
  margin-right: 10px;
`;

const EducationDegree = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const EducationInstitution = styled.p`
  font-size: 14px;
  color: #666;
  margin: 5px 0;
  font-weight: 500;
`;

const EducationPeriod = styled.p`
  font-size: 13px;
  color: #888;
  margin: 5px 0 10px 0;
`;

const EducationDescription = styled.p`
  font-size: 14px;
  color: #666;
  line-height: 1.5;
`;

const AwardsSection = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 15px;
  margin-top: 20px;
`;

const AwardsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const AwardItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 8px;
  font-size: 14px;
  color: #666;

  &::before {
    content: "🏆";
    margin-right: 8px;
  }
`;

export default CreatorProfilePage;
